import http from "../http-common";
import { CUSTOMER_TYPE, PERSONAL_ROUTE_STATUS, PICKUP_STATUS } from "../shared/constants";

const normalizeCustomerType = (value) => `${value || ''}`.trim().toLowerCase().replace(/\s+/g, '_');

const isMemberLikeCustomerType = (value) => {
  const normalizedType = normalizeCustomerType(value);
  return normalizedType === '' || [CUSTOMER_TYPE.MEMBER, CUSTOMER_TYPE.SELF_PAY].includes(normalizedType);
};

const isVisitorCustomerType = (value) => normalizeCustomerType(value) === CUSTOMER_TYPE.VISITOR;

const isCustomerCheckedOut = (status) => {
  return (
    status === PERSONAL_ROUTE_STATUS.DROPPED_OFF ||
    status === PERSONAL_ROUTE_STATUS.LEFT_CENTER ||
    status === PERSONAL_ROUTE_STATUS.SKIP_DROPOFF
  );
};

/**
 * Manifest and check-in / check-out counts aligned with trans-routes RoutesSection (per route list).
 * Excludes pickup schedule-absent and disabled route rows; dedupes by customer_id.
 * @param {'inbound'|'outbound'} [routeKind] — inbound: Checked In = IN_CENTER; outbound: Checked Out = dropped/left/skip.
 */
const getRouteSectionManifestStats = (transRoutes, routeKind = 'inbound') => {
  const routeCount = (transRoutes || []).length;
  const seniors = [];
  for (const transRoute of transRoutes || []) {
    for (const customer of transRoute.route_customer_list || []) {
      const customerId = customer?.customer_id;
      if (seniors.some((item) => item?.customer_id === customerId)) continue;
      if (customer.customer_pickup_status === PICKUP_STATUS.SCHEDULE_ABSENT) continue;
      if (customer.customer_route_status === PERSONAL_ROUTE_STATUS.DISABLED) continue;
      seniors.push(customer);
    }
  }
  const scheduledTotal = seniors.length;
  const scheduledAbsentCount = seniors.filter(
    (item) => item?.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT
  ).length;
  const unexpectedAbsentCount = seniors.filter(
    (item) => item?.customer_route_status === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT
  ).length;
  const manifestMembers = seniors.filter((item) => isMemberLikeCustomerType(item?.customer_type)).length;
  const manifestVisitors = seniors.filter((item) => isVisitorCustomerType(item?.customer_type)).length;

  if (routeKind === 'outbound') {
    const checkedOutSeniors = seniors.filter(
      (item) =>
        item?.customer_route_status !== PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT &&
        item?.customer_route_status !== PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT &&
        isCustomerCheckedOut(item?.customer_route_status)
    );
    const checkedOutCount = checkedOutSeniors.length;
    const checkedOutMembers = checkedOutSeniors.filter((item) => isMemberLikeCustomerType(item?.customer_type)).length;
    const checkedOutVisitors = checkedOutSeniors.filter((item) => isVisitorCustomerType(item?.customer_type)).length;

    return {
      routeCount,
      scheduledTotal,
      scheduledAbsentCount,
      unexpectedAbsentCount,
      checkedInCount: 0,
      checkedInMembers: 0,
      checkedInVisitors: 0,
      manifestMembers,
      manifestVisitors,
      checkedOutCount,
      checkedOutMembers,
      checkedOutVisitors,
    };
  }

  const checkedInSeniors = seniors.filter(
    (item) => item?.customer_route_status === PERSONAL_ROUTE_STATUS.IN_CENTER
  );
  const checkedInCount = checkedInSeniors.length;
  const checkedInMembers = checkedInSeniors.filter((item) => isMemberLikeCustomerType(item?.customer_type)).length;
  const checkedInVisitors = checkedInSeniors.filter((item) => isVisitorCustomerType(item?.customer_type)).length;

  return {
    routeCount,
    scheduledTotal,
    scheduledAbsentCount,
    unexpectedAbsentCount,
    checkedInCount,
    manifestMembers,
    manifestVisitors,
    checkedInMembers,
    checkedInVisitors,
    checkedOutCount: 0,
    checkedOutMembers: 0,
    checkedOutVisitors: 0,
  };
};
const getAll = (scheduleDate, driverId) => {
  const params = {}
  if (driverId) {
    params.driverId = driverId;
  }
  if (scheduleDate) {
    params.scheduleDate = scheduleDate
  }
  return http.get('/routes', {
    params
  });
};

const getAllRoutesOnAndAfterToday = () => {
  return http.get('/routes/on-and-after-today');
};

const getAttendanceCounts = (startDate, endDate) => {
  return http.get('/routes/attendance-counts', {
    params: { startDate, endDate },
  });
};

const updateRoute = (id, data) => {
	return http.put(`/routes/${id}`, data, {
		params: { isWeb: true },
	});
};

const getRoute = (id) => {
  return http.get(`/routes/${id}`);
}

const createNewRoute = (data) => {
	return http.post('/routes', data, {
		params: { isWeb: true },
	});
};

const deleteRoute = (id) => {
	return http.delete(`/routes/${id}`);
}

const getAllBreakfastRecords = (date) => {
  const params = { date }
  return http.get('/breakfasts', {
    params
  })
}

const createBreakfastRecords = (data) => {
  return http.post('/breakfasts', data);
}

const deleteBreakfastRecords = (id) => {
  return http.delete(`/breakfasts/${id}`);
}

const getAllLunchRecords = (date) => {
  const params = { date }
  return http.get('/lunches', {
    params
  })
}

const updateInProgress = (data) => {
  return http.post('/routes/update-in-progress', data);
}

const createLunchRecords = (data) => {
  return http.post('/lunches', data);
}

const deleteLunchRecords = (id) => {
  return http.delete(`/lunches/${id}`);
}

const getAllSnackRecords = (date) => {
  const params = { date }
  return http.get('/snacks', {
    params
  })
}

const createSnackRecords = (data) => {
  return http.post('/snacks', data);
}

const deleteSnackRecords = (id) => {
  return http.delete(`/snacks/${id}`);
}

const getAllInCenterCustomersFromRoutes = (routes, breakfasts) => {
  let result = [];
  for (const route of [...routes]) {
    const customerList = route.route_customer_list.filter((i) => (i.customer_route_status === PERSONAL_ROUTE_STATUS.IN_CENTER) && !result.find(r => r.customer_id === i.customer_id))?.map(item => Object.assign({}, item, { has_breakfast: !!(breakfasts.find(b => b.customer_id === item.customer_id && b.has_breakfast)) }));
    result = result.concat(customerList);
  }
  return result;
}

const getAllInCenterCustomersFromRoutesForLunch = (routes, lunches) => {
  let result = [];
  for (const route of [...routes]) {
    const customerList = route.route_customer_list.filter((i) => (i.customer_route_status === PERSONAL_ROUTE_STATUS.IN_CENTER) && !result.find(r => r.customer_id === i.customer_id))?.map(item => Object.assign({}, item, { has_lunch: !!(lunches.find(b => b.customer_id === item.customer_id && b.has_lunch)) }));
    result = result.concat(customerList);
  }
  return result;
}

const getAllInCenterCustomersFromRoutesForSnack = (routes, snacks) => {
  let result = [];
  for (const route of [...routes]) {
    const customerList = route.route_customer_list.filter((i) => (i.customer_route_status === PERSONAL_ROUTE_STATUS.IN_CENTER) && !result.find(r => r.customer_id === i.customer_id))?.map(item => Object.assign({}, item, { has_snack: !!(snacks.find(b => b.customer_id === item.customer_id && b.has_snack)) }));
    result = result.concat(customerList);
  }
  return result;
}

const getAllCustomersFromRoutes = (routes, vehicles) => {
  let result = [];
  for (const route of [...routes]) {
    const customerList = route.route_customer_list.filter((i) => !result.find(r => r.customer_id === i.customer_id))?.map(item => Object.assign({}, item, { vehicle_number: vehicles.find(vehicle => vehicle.id === route.vehicle)?.vehicle_number }));
    result = result.concat(customerList);
  }
  return result;
};

/** Inbound customers with route context (same row rules as schedule personnel inbound report). */
const getAllRouteCustomersWithContext = (routes) => {
  let result = [];
  for (const route of routes || []) {
    const list = (route.route_customer_list || []).map((item) =>
      Object.assign({}, item, { routeType: route.type, routeId: route.id, route: route })
    );
    result = result.concat(list);
  }
  return result.sort((a, b) => (a.customer_id < b.customer_id ? -1 : 1));
};

/**
 * Worksheet matrix for "Route Inbound Customers Show-up" / inbound participants Excel export.
 * @param {Array} transRoutes - routes for the selected date
 * @param {Array} [vehicles] - active vehicles (for vehicle number column)
 * @returns {Array<Array>} rows for downloadXlsxFromMatrix
 */
const buildInboundParticipantsReportMatrix = (transRoutes, vehicles) => {
  const head = ['No.', 'Name', 'Show-Up', 'Vehicle Number'];
  const chineseHead = ['序号', '姓名', '出勤', '车号'];
  const content = [];
  let index = 1;
  const inboundRoutes = (transRoutes || []).filter((route) => route.type === 'inbound');
  const customersList = getAllRouteCustomersWithContext(inboundRoutes).filter(
    (customer) =>
      customer.customer_pickup_status !== PICKUP_STATUS.SCHEDULE_ABSENT &&
      ![
        PERSONAL_ROUTE_STATUS.NO_STATUS,
        PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT,
        PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT,
      ].includes(customer.customer_route_status)
  );
  for (let i = 0; i < customersList.length; i += 1) {
    const c = customersList[i];
    const vehNum = vehicles?.find((item) => item?.id === c?.route?.vehicle)?.vehicle_number;
    content.push([
      index,
      c.customer_name,
      c.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED ? 'Y' : 'N',
      vehNum,
    ]);
    index += 1;
  }
  const finalNumber = [
    'Participants Arrived:',
    customersList.filter((customer) => customer.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED).length,
  ];
  return [head, chineseHead, ...content, finalNumber];
};

export const TransRoutesService = {
  getAll,
	updateRoute,
	createNewRoute,
	deleteRoute,
  getAllBreakfastRecords,
  createBreakfastRecords,
  deleteBreakfastRecords,
  getAllInCenterCustomersFromRoutes,
  getAllCustomersFromRoutes,
  buildInboundParticipantsReportMatrix,
  getRouteSectionManifestStats,
  getAllLunchRecords,
  createLunchRecords,
  deleteLunchRecords,
  getAllInCenterCustomersFromRoutesForLunch,
  getAllInCenterCustomersFromRoutesForSnack,
  getAllSnackRecords,
  createSnackRecords,
  deleteSnackRecords,
  updateInProgress,
  getRoute,
  getAllRoutesOnAndAfterToday,
  getAttendanceCounts,
};
